import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { retry, catchError, tap } from 'rxjs/operators';
import { ApiService } from './api.service';
import { Notification, NotificationType, NotificationPreferences } from '../interfaces/api-interfaces';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private readonly http = inject(HttpClient);
  private readonly apiService = inject(ApiService);
  private readonly maxRetries = 1;

  private get apiUrl() {
    return `${this.apiService.baseUrl}/notifications`;
  }

  // Observable state
  private notificationsSubject = new BehaviorSubject<Notification[]>([]);
  private unreadCountSubject = new BehaviorSubject<number>(0);
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private locallyReadIds = new Set<string>();

  // Public observables
  public notifications$ = this.notificationsSubject.asObservable();
  public unreadCount$ = this.unreadCountSubject.asObservable();
  public loading$ = this.loadingSubject.asObservable();

  // Polling state
  private pollInterval?: number;
  private isPolling = false;

  // User notification preferences for client-side filtering
  private prefs: NotificationPreferences | null = null;

  // =============================================================================
  // NOTIFICATION MANAGEMENT
  // =============================================================================

  /**
   * Get list of user's notifications
   * GET /api/notifications
   */
  getNotifications(): Observable<Notification[]> {
    this.loadingSubject.next(true);

    return this.http.get<Notification[]>(this.apiUrl).pipe(
      retry(this.maxRetries),
      tap((result: any) => {
        const raw: Notification[] = Array.isArray(result) ? result : result?.notifications ?? result?.items ?? [];
        const notifications = raw
          .filter(n => !this.isMuted(n))
          .map(n => this.locallyReadIds.has(n.notificationId) ? { ...n, read: true } : n);
        this.notificationsSubject.next(notifications);
        this.updateUnreadCount(notifications);
        this.loadingSubject.next(false);
      }),
      catchError((error) => {
        console.error('Error fetching notifications:', error);
        this.loadingSubject.next(false);
        throw error;
      })
    );
  }

  /**
   * Mark notification as read
   * PATCH /api/notifications/:id/read
   */
  markAsRead(notificationId: string): Observable<{ message: string }> {
    return this.http.patch<{ message: string }>(
      `${this.apiUrl}/${notificationId}/read`,
      {}
    ).pipe(
      retry(this.maxRetries),
      tap(() => {
        this.locallyReadIds.add(notificationId);
        const notifications = this.notificationsSubject.value;
        const updatedNotifications = notifications.map(n =>
          n.notificationId === notificationId ? { ...n, read: true } : n
        );
        this.notificationsSubject.next(updatedNotifications);
        this.updateUnreadCount(updatedNotifications);
      }),
      catchError((error) => {
        console.error('Error marking notification as read:', error);
        throw error;
      })
    );
  }

  /**
   * Mark all notifications as read
   * PATCH /api/notifications/read-all
   */
  markAllAsRead(): Observable<{ message: string }> {
    return this.http.patch<{ message: string }>(
      `${this.apiUrl}/read-all`,
      {}
    ).pipe(
      retry(this.maxRetries),
      tap(() => {
        const notifications = this.notificationsSubject.value;
        notifications.forEach(n => this.locallyReadIds.add(n.notificationId));
        const updatedNotifications = notifications.map(n => ({ ...n, read: true }));
        this.notificationsSubject.next(updatedNotifications);
        this.updateUnreadCount(updatedNotifications);
      }),
      catchError((error) => {
        console.error('Error marking all notifications as read:', error);
        throw error;
      })
    );
  }

  /**
   * Dismiss (delete) notification
   * DELETE /api/notifications/:id
   */
  dismissNotification(notificationId: string): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(
      `${this.apiUrl}/${notificationId}`
    ).pipe(
      retry(this.maxRetries),
      tap(() => {
        // Remove from local state
        const notifications = this.notificationsSubject.value;
        const updatedNotifications = notifications.filter(n => n.notificationId !== notificationId);
        this.notificationsSubject.next(updatedNotifications);
        this.updateUnreadCount(updatedNotifications);
      }),
      catchError((error) => {
        console.error('Error dismissing notification:', error);
        throw error;
      })
    );
  }

  // =============================================================================
  // POLLING
  // =============================================================================

  /**
   * Load and cache the user's notification preferences for client-side filtering.
   * Called once when polling starts; refreshed every 5 minutes.
   */
  private async loadPrefs(): Promise<void> {
    try {
      const user = await firstValueFrom(this.apiService.getUserProfile());
      this.prefs = user.notificationPreferences ?? null;
    } catch { /* keep previous prefs */ }
  }

  /** Returns true if a notification should be shown based on mute preferences. */
  private isMuted(n: Notification): boolean {
    if (!this.prefs) return false;
    const meta = n.metadata;

    if (n.type === NotificationType.DIRECT_MESSAGE) {
      return this.prefs.types.direct_message === false;
    }
    if (n.type === NotificationType.CHANNEL_MESSAGE || n.type === NotificationType.ANNOUNCEMENT) {
      if (meta?.channelId && (this.prefs.mutedChannels ?? []).includes(meta.channelId)) return true;
      if (meta?.communityId && (this.prefs.mutedCommunities ?? []).includes(meta.communityId)) return true;
      return false;
    }
    if (n.type === NotificationType.BUDDY_MESSAGE) {
      if (meta?.buddyGroupId && (this.prefs.mutedBuddyGroups ?? []).includes(meta.buddyGroupId)) return true;
      return false;
    }
    return false;
  }

  /**
   * Start polling for new notifications (5-second interval)
   */
  startPolling(): void {
    if (this.isPolling) {
      console.warn('Notification polling already started');
      return;
    }

    this.isPolling = true;

    // Load preferences first, then start polling
    this.loadPrefs().then(() => {
      this.getNotifications().subscribe({
        error: (error) => {
          console.error('Error in initial notification load:', error);
        }
      });
    });

    // Poll every 5 seconds
    this.pollInterval = window.setInterval(() => {
      this.getNotifications().subscribe({
        error: (error) => {
          console.warn('Polling error:', error);
        }
      });
    }, 5000);

    console.log('Notification polling started');
  }

  /**
   * Stop polling for notifications
   */
  stopPolling(): void {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = undefined;
      this.isPolling = false;
      console.log('Notification polling stopped');
    }
  }

  /**
   * Check if polling is active
   */
  isPollingActive(): boolean {
    return this.isPolling;
  }

  // =============================================================================
  // HELPER METHODS
  // =============================================================================

  /**
   * Update unread count based on notifications array
   */
  private updateUnreadCount(notifications: Notification[]): void {
    const unreadCount = notifications.filter(n => !n.read).length;
    this.unreadCountSubject.next(unreadCount);
  }

  /**
   * Get current notifications (synchronous)
   */
  getCurrentNotifications(): Notification[] {
    return this.notificationsSubject.value;
  }

  /**
   * Get current unread count (synchronous)
   */
  getCurrentUnreadCount(): number {
    return this.unreadCountSubject.value;
  }

  /**
   * Mark all unread notifications for a DM conversation or buddy group as read locally + via API
   */
  markReadForSource(sourceType: 'dm' | 'buddy_group' | 'channel', id: string): void {
    const notifications = this.notificationsSubject.value;
    const toMark = notifications.filter(n => {
      if (n.read) return false;
      if (sourceType === 'buddy_group') {
        const actionGroupId = n.actionUrl?.match(/^\/buddy-groups\/([^/]+)/)?.[1];
        return n.metadata?.buddyGroupId === id || actionGroupId === id;
      }
      if (sourceType === 'channel') {
        const actionChannelId = n.actionUrl?.match(/\/channel\/([^/?]+)/)?.[1];
        return n.metadata?.channelId === id || actionChannelId === id;
      }
      if (sourceType === 'dm') {
        const actionConvId = n.actionUrl?.match(/^\/messages\/([^/]+)/)?.[1];
        return n.referenceId === id || n.metadata?.conversationId === id || actionConvId === id;
      }
      return false;
    });
    if (toMark.length === 0) return;

    const updated = notifications.map(n =>
      toMark.some(m => m.notificationId === n.notificationId) ? { ...n, read: true } : n
    );
    toMark.forEach(n => this.locallyReadIds.add(n.notificationId));
    this.notificationsSubject.next(updated);
    this.updateUnreadCount(updated);

    toMark.forEach(n => this.markAsRead(n.notificationId).subscribe({ error: () => {} }));
  }

  /**
   * Reload preferences and re-filter notifications (call after saving notification settings).
   */
  async refreshPrefs(): Promise<void> {
    await this.loadPrefs();
    this.getNotifications().subscribe({ error: () => {} });
  }

  /**
   * Clear all notifications from state (useful for logout)
   */
  clearNotifications(): void {
    this.locallyReadIds.clear();
    this.notificationsSubject.next([]);
    this.unreadCountSubject.next(0);
  }
}
